import type { ListAnswerWithCursorResponse } from '@coorum-research/shared-common/api-response'
import type { SearchTagAnswerListQuery } from '@coorum-research/shared-common/types/answer.type'
import type { CursorPaginationQuery } from '@coorum-research/shared-common/types/page-info.type'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import { AnswersListItem } from '@coorum-research/core/src/components/AnswersListItem'
import { Spinner } from '@coorum-research/core/src/components/Spinner'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Fragment, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { answerApi } from '../apis/answers.api'

type Props = {
  query: SearchTagAnswerListQuery
  theme?: ThemeConfig
  onItemSelected?: (item: ListAnswerWithCursorResponse['items'][number]) => void
  onItemIconClick?: (item: ListAnswerWithCursorResponse['items'][number]) => void
}

export function AnswersList({ query, theme, onItemSelected, onItemIconClick }: Props) {
  const { listAnswer } = answerApi()

  const {
    data,
    isFetched,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['user-tagged-answers', query],
    queryFn: async ({ pageParam }) => {
      return listAnswer({ ...query, ...pageParam })
    },
    initialPageParam: undefined as CursorPaginationQuery | undefined,
    getNextPageParam: ({ pageInfo }): CursorPaginationQuery | undefined => (pageInfo.hasNextPage && pageInfo.endCursor ? { after: pageInfo.endCursor } : undefined),
    getPreviousPageParam: ({ pageInfo }): CursorPaginationQuery | undefined => (pageInfo.hasBeforePage && pageInfo.startCursor ? { before: pageInfo.startCursor } : undefined),
  })

  const { ref: inViewRef, inView } = useInView()

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView])

  if (!isFetched) {
    return (
      <div className="flex h-[68px] w-full items-center justify-center">
        <Spinner fill={theme ? theme.mainColor : undefined} />
      </div>
    )
  }

  const answerPages = data?.pages ?? []
  const answersCount = answerPages.reduce((count, { items }) => count + items.length, 0)

  return (
    <>
      {answersCount > 0
        ? (
          <>
            {answerPages.map(({ items }, i) => (
              <Fragment key={i}>
                {items.map((item, j) => (
                  <AnswersListItem
                    key={item.id}
                    item={item}
                    isFirst={i === 0 && j === 0}
                    onItemClick={onItemSelected}
                    onIconClick={onItemIconClick}
                  />
                ))}
              </Fragment>
            ))}
            {hasNextPage
              && (
                <div ref={inViewRef} className="flex h-[68px] w-full items-center justify-center">
                  {isFetchingNextPage && <Spinner fill={theme ? theme.mainColor : undefined} />}
                </div>
              )}
          </>
        )
        : (
          <div className="flex items-center justify-center p-6 text-gray-500">回答がありません</div>
        )}
    </>
  )
}
